import React, { useEffect, useState } from "react";
import supplyService from "../../Services/supplyService";
import { useWarehouse } from "../Layout/WarehouseContext";
import { cbFmtINR, cbFmtNum } from "./supplyData";
import { fmtDateTime } from "../../Utils/dateFormat";
import {
  Button,
  Pill,
  Table,
  Tr,
  Td,
  SectionHeader,
  ToolbarBar,
  TabBar,
  Eyebrow,
  EmptyState,
  LoadingState,
  ErrorState,
  InlineAlert,
  IconDownload,
  IconEye,
  IconX,
} from "./supplyKit";

const statusPillVariant = (status) => {
  if (status === "INVOICED") return "green";
  if (status === "CANCELLED" || status === "REJECTED") return "red";
  if (status === "ORDERED") return "amber";
  return "neutral";
};

const errMsg = (e, fallback) => e?.response?.data?.message || e?.message || fallback;


const orderRef = (o) =>
  (o.orderIds && o.orderIds.length ? o.orderIds.join(", ") : o.orderRetrieveId) || "—";

const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        field += '"';
        i += 1;
      } else if (ch === '"') {
        quoted = false;
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i += 1;
      row.push(field);
      if (row.some((c) => c !== "")) rows.push(row);
      row = [];
      field = "";
    } else {
      field += ch;
    }
  }
  row.push(field);
  if (row.some((c) => c !== "")) rows.push(row);
  const [header = [], ...body] = rows;
  return body.map((r) => Object.fromEntries(header.map((h, i) => [h.trim(), r[i] ?? ""])));
};

const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

const PurchaseInvoice = () => {
  const { code: wh } = useWarehouse();
  const [tab, setTab] = useState("ORDERS");
  const [selected, setSelected] = useState(null);

  const [orders, setOrders] = useState([]);
  const [ordersPage, setOrdersPage] = useState(0);
  const [ordersNext, setOrdersNext] = useState(null);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState(null);

  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoicesError, setInvoicesError] = useState(null);

  const [shopName, setShopName] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setOrdersPage(0);
    setSelected(null);
  }, [wh]);

  useEffect(() => {
    if (!wh) return undefined;
    let alive = true;
    setOrdersLoading(true);
    setOrdersError(null);
    supplyService
      .listSaveoOrders(wh, ordersPage)
      .then((d) => {
        if (!alive) return;
        setOrders(d.orders || []);
        setOrdersNext(d.nextPage ?? null);
        if (d.shopName) setShopName(d.shopName);
        setOrdersLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setOrdersError(errMsg(e, "Could not load orders."));
        setOrdersLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [wh, ordersPage, reloadKey]);

  useEffect(() => {
    if (!wh) return undefined;
    let alive = true;
    setInvoicesLoading(true);
    setInvoicesError(null);
    supplyService
      .listSaveoInvoices(wh)
      .then((d) => {
        if (!alive) return;
        setInvoices(d.orders || []);
        if (d.shopName) setShopName(d.shopName);
        setInvoicesLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setInvoicesError(errMsg(e, "Could not load invoices."));
        setInvoicesLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [wh, reloadKey]);

  if (!wh) {
    return (
      <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <SectionHeader title="Purchase Invoice" sub="Saveo distributor orders and invoices" />
        <EmptyState
          title="Pick a store"
          hint="Purchase invoices are kept per store. Choose a store from the selector in the top bar."
        />
      </div>
    );
  }

  const isOrders = tab === "ORDERS";
  const rows = isOrders ? orders : invoices;
  const loading = isOrders ? ordersLoading : invoicesLoading;
  const error = isOrders ? ordersError : invoicesError;

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader
        title="Purchase Invoice"
        sub={`Saveo distributor orders and invoices${shopName ? ` · ${shopName}` : ""}`}
        actions={<Button onClick={() => setReloadKey((k) => k + 1)}>Refresh</Button>}
      />

      <TabBar
        value={tab}
        onChange={(v) => {
          setTab(v);
          setSelected(null);
        }}
        tabs={[
          { id: "ORDERS", label: "Orders" },
          { id: "INVOICES", label: "Invoices", count: invoicesLoading ? undefined : invoices.length },
        ]}
      />

      {loading && rows.length === 0 ? (
        <LoadingState label={isOrders ? "Loading orders…" : "Loading invoices…"} />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <div
          className={selected ? "grid-cols-1 lg:grid-cols-[1fr_560px]" : "grid-cols-1"}
          style={{ display: "grid", gap: 12, alignItems: "start" }}
        >
          <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 8 }}>
            <Table
              columns={[
                { label: "Order #" },
                { label: "Ordered at" },
                { label: "Delivery" },
                ...(isOrders ? [{ label: "Payment" }] : []),
                { label: "Invoice #" },
                { label: "Invoiced at" },
                { label: "Amount", align: "right" },
                { label: "Status" },
                { label: "", align: "right" },
              ]}
            >
              {rows.map((o, idx) => {
                const key = o.buyerInvoiceId || `${orderRef(o)}-${idx}`;
                const isSel = selected && selected.buyerInvoiceId === o.buyerInvoiceId && o.buyerInvoiceId;
                return (
                  <Tr
                    key={key}
                    selected={!!isSel}
                    onClick={o.buyerInvoiceId ? () => setSelected(isSel ? null : o) : undefined}
                  >
                    <Td mono style={{ fontWeight: 600 }}>
                      {orderRef(o)}
                    </Td>
                    <Td mono>{fmtDateTime(o.orderedAt) || "—"}</Td>
                    <Td>
                      {o.deliveryDate || "—"}
                      {o.deliverySlot && (
                        <div style={{ fontSize: 10, color: "var(--fg-muted)", marginTop: 2 }}>
                          {o.deliverySlot}
                        </div>
                      )}
                    </Td>
                    {isOrders && <Td>{o.paymentMode || "—"}</Td>}
                    <Td mono>
                      {o.invoiceNumber || "—"}
                      {o.manualBill && (
                        <Pill variant="neutral" style={{ marginLeft: 6 }}>
                          Manual
                        </Pill>
                      )}
                    </Td>
                    <Td mono>{fmtDateTime(o.invoicedAt) || "—"}</Td>
                    <Td mono right style={{ fontWeight: 700, color: "var(--fg-strong)" }}>
                      {o.invoiceAmount != null ? cbFmtINR(o.invoiceAmount) : "—"}
                    </Td>
                    <Td>
                      <Pill variant={statusPillVariant(o.status)} dot>
                        {o.status || "—"}
                      </Pill>
                    </Td>
                    <Td right>
                      {o.buyerInvoiceId && (
                        <div
                          style={{ display: "inline-flex", gap: 6 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button size="sm" icon={<IconEye size={12} />} onClick={() => setSelected(o)}>
                            View
                          </Button>
                          <DownloadButton wh={wh} invoice={o} />
                        </div>
                      )}
                    </Td>
                  </Tr>
                );
              })}
              {rows.length === 0 && (
                <Tr>
                  <Td
                    colSpan={isOrders ? 9 : 8}
                    style={{ textAlign: "center", padding: 28, color: "var(--fg-muted)", fontSize: 12 }}
                  >
                    {isOrders ? "No orders for this store." : "No invoices for this store yet."}
                  </Td>
                </Tr>
              )}
            </Table>

            {isOrders && (
              <ToolbarBar>
                <div
                  style={{
                    marginLeft: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                    {ordersLoading ? "Loading… · " : ""}page {ordersPage + 1}
                  </span>
                  <Button
                    size="sm"
                    disabled={ordersLoading || ordersPage <= 0}
                    onClick={() => setOrdersPage((p) => Math.max(0, p - 1))}
                  >
                    ← Prev
                  </Button>
                  <Button
                    size="sm"
                    disabled={ordersLoading || ordersNext == null}
                    onClick={() => setOrdersPage(ordersNext)}
                  >
                    Next →
                  </Button>
                </div>
              </ToolbarBar>
            )}
          </div>

          {selected && (
            <InvoiceDetail wh={wh} invoice={selected} onClose={() => setSelected(null)} />
          )}
        </div>
      )}
    </div>
  );
};

const DownloadButton = ({ wh, invoice, size = "sm" }) => {
  const [busy, setBusy] = useState(false);
  const onClick = () => {
    setBusy(true);
    supplyService
      .downloadSaveoInvoice(wh, invoice.buyerInvoiceId)
      .then(({ blob, filename }) => {
        saveBlob(blob, filename || `Invoice ${invoice.invoiceNumber || invoice.buyerInvoiceId}.csv`);
      })
      .catch((e) => window.alert(errMsg(e, "Could not download the invoice.")))
      .finally(() => setBusy(false));
  };
  return (
    <Button size={size} icon={<IconDownload size={12} />} disabled={busy} onClick={onClick}>
      {busy ? "Downloading…" : "Download"}
    </Button>
  );
};

const InvoiceDetail = ({ wh, invoice, onClose }) => {
  const [lines, setLines] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLines(null);
    setError(null);
    supplyService
      .downloadSaveoInvoice(wh, invoice.buyerInvoiceId)
      .then(({ blob }) => blob.text())
      .then((text) => {
        if (alive) setLines(parseCsv(text));
      })
      .catch((e) => {
        if (alive) setError(errMsg(e, "Could not load the invoice."));
      });
    return () => {
      alive = false;
    };
  }, [wh, invoice.buyerInvoiceId]);

  const total = (lines || []).reduce((s, l) => s + (Number(l.NetValue) || 0), 0);
  const units = (lines || []).reduce((s, l) => s + (Number(l.Quantity) || 0), 0);

  return (
    <aside style={detailAside} className="min-w-0 lg:sticky lg:top-3 lg:max-h-[calc(100vh_-_130px)]">
      <div style={detailHeader}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <Eyebrow>Purchase invoice</Eyebrow>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 13,
              fontWeight: 700,
              color: "var(--fg-strong)",
              marginTop: 2,
            }}
          >
            {invoice.invoiceNumber || invoice.buyerInvoiceId}
          </div>
          <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>
            Order {orderRef(invoice)} · invoiced {fmtDateTime(invoice.invoicedAt) || "—"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <DownloadButton wh={wh} invoice={invoice} />
          <Button size="sm" variant="ghost" icon={<IconX size={12} />} onClick={onClose} />
        </div>
      </div>

      <div className="cb-scroll" style={{ padding: 12, overflow: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
        {error ? (
          <InlineAlert>{error}</InlineAlert>
        ) : lines == null ? (
          <LoadingState label="Loading invoice…" />
        ) : (
          <>
            <Table
              columns={[
                { label: "Product" },
                { label: "Batch" },
                { label: "Expiry" },
                { label: "Qty", align: "right" },
                { label: "PTR", align: "right" },
                { label: "MRP", align: "right" },
                { label: "GST %", align: "right" },
                { label: "Net", align: "right" },
              ]}
            >
              {lines.map((l, i) => (
                <Tr key={`${l["Product Code"]}-${l["Batch Number"]}-${i}`}>
                  <Td style={{ whiteSpace: "normal", padding: "6px 12px" }}>
                    <div style={{ fontWeight: 600, color: "var(--fg-strong)" }}>{l["Product Name"]}</div>
                    <div style={{ fontSize: 10, color: "var(--fg-muted)", marginTop: 2 }}>
                      {l["Manufacturer Name"]}
                    </div>
                  </Td>
                  <Td mono>{l["Batch Number"]}</Td>
                  <Td mono>{l["Expiry Date"]}</Td>
                  <Td mono right>{l.Quantity}</Td>
                  <Td mono right>{cbFmtINR(l.PTR)}</Td>
                  <Td mono right>{cbFmtINR(l.MRP)}</Td>
                  <Td mono right>{l.Tax}</Td>
                  <Td mono right style={{ fontWeight: 600 }}>{cbFmtINR(l.NetValue)}</Td>
                </Tr>
              ))}
              {lines.length === 0 && (
                <Tr>
                  <Td colSpan={8} style={{ textAlign: "center", padding: 20, color: "var(--fg-muted)" }}>
                    This invoice has no line items.
                  </Td>
                </Tr>
              )}
            </Table>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 12,
                color: "var(--fg-muted)",
                fontFamily: "var(--font-mono)",
              }}
            >
              <span>
                {lines.length} items · {cbFmtNum(units)} units
              </span>
              <span style={{ fontWeight: 700, color: "var(--fg-strong)" }}>
                {cbFmtINR(invoice.invoiceAmount || total)}
              </span>
            </div>
          </>
        )}
      </div>
    </aside>
  );
};

const detailAside = {
  border: "1px solid var(--border-default)",
  borderRadius: 6,
  background: "var(--bg-panel)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};
const detailHeader = {
  padding: "12px 16px",
  borderBottom: "1px solid var(--border-default)",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 8,
};

export default PurchaseInvoice;
